import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../../config/URL";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import {
  ThemeProvider,
  createTheme,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import DocumentEdit from "./DocumentEdit";
import fetchAllClassesWithIdsC from "../List/ClassListByCourse";
import fetchAllCoursesWithIdsC from "../List/CourseListByCenter";
import fetchAllTeacherListByCenter from "../List/TeacherListByCenter";
import { toast } from "react-toastify";
import fetchAllCentersWithIds from "../List/CenterList";
import GlobalDelete from "../../components/common/GlobalDelete";
import fetchAllCoursesWithIds from "../List/CourseList";
import fetchAllTeachersWithIds from "../List/TeacherList";
import { exportVisibleColumnsToCSV } from "../../components/common/ExportCsv";

const Document = ({ selectedCenter }) => {
  const [filters, setFilters] = useState({
    centerId: "",
    courseId: "",
    classId: "",
    userId: "",
    day: "",
    date: "",
  });
  const [data, setData] = useState([]);
  const [centerData, setCenterData] = useState([]);
  const centerLocalId = localStorage.getItem("selectedCenterId");
  const [courseData, setCourseData] = useState([]);
  const [classData, setClassData] = useState([]);
  const [teacherData, setTeacherData] = useState([]);
  const [courseListData, setCourseListData] = useState([]);
  const [teacherListData, setTeacherListData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [isClearFilterClicked, setIsClearFilterClicked] = useState(false);

  /* -------------------- ROLE MATRIX -------------------- */
  const storedScreens = JSON.parse(localStorage.getItem("screens") || {});
  const canIndex = Boolean(storedScreens?.documentFileIndex);
  const canRead = Boolean(storedScreens?.documentFileRead);
  const canCreate = Boolean(storedScreens?.documentFileCreate);
  const canUpdate = Boolean(storedScreens?.documentFileUpdate);
  const canDelete = Boolean(storedScreens?.documentFileDelete);
  const showActionColumn = canUpdate || canDelete;

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return date.toDateString();
  };

  const isExpired = (expiredDate) => {
    if (!expiredDate) return false;
    return new Date(expiredDate) < new Date();
  };

  /* -------------------- COLUMNS -------------------- */
  const columns = useMemo(() => {
    const baseColumns = [
      {
        accessorFn: (row, index) => index + 1,
        header: "S.NO",
        size: 40,
        Cell: ({ cell }) => (
          <span style={{ textAlign: "center" }}>{cell.getValue()}</span>
        ),
      },
      { accessorKey: "folderName", header: "Folder Name" },
      {
        header: "Status",
        size: 80,
        Cell: ({ row }) =>
          isExpired(row.original.expiredDate) ? (
            <span className="badge bg-danger">Expired</span>
          ) : (
            <span className="badge bg-success">Active</span>
          ),
      },
      { accessorKey: "studentName", header: "Student Name" },
      { accessorKey: "user", header: "User Name" },
      { accessorKey: "course", header: "Course" },
      { accessorKey: "classListing", header: "Class" },
      { accessorKey: "batchTime", header: "Batch" },
      {
        accessorKey: "date",
        header: "Date",
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      {
        accessorKey: "expiredDate",
        header: "Expired Date",
        Cell: ({ cell }) => formatDate(cell.getValue()),
      },
      { accessorKey: "day", header: "Days" },
      { accessorKey: "folderCategory", header: "Folder Category" },
      { accessorKey: "createdBy", header: "Created By" },
      {
        accessorKey: "createdAt",
        header: "Created On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10),
      },
      {
        accessorKey: "updatedAt",
        header: "Updated On",
        Cell: ({ cell }) => cell.getValue()?.substring(0, 10) || "",
      },
      { accessorKey: "updatedBy", header: "Updated By" },
    ];

    if (!showActionColumn) return baseColumns;

    const actionColumn = {
      id: "actions",
      header: "",
      size: 20,
      enableSorting: false,
      Cell: ({ row }) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            setMenuAnchor(e.currentTarget);
            setSelectedId(row.original.id);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    };

    return [baseColumns[0], actionColumn, ...baseColumns.slice(1)];
  }, [showActionColumn]);

  /* -------------------- THEME -------------------- */
  const theme = createTheme({
    components: {
      MuiTableCell: {
        styleOverrides: {
          head: {
            color: "#535454 !important",
            backgroundColor: "#e6edf7 !important",
            fontWeight: "400 !important",
            fontSize: "13px !important",
            textAlign: "center !important",
          },
        },
      },
    },
  });
  const fetchListWithCenterIdData = async (centerId) => {
    try {
      const courseDatas = await fetchAllCoursesWithIdsC(centerId);
      const teacherDatas = await fetchAllTeacherListByCenter(centerId);
      setTeacherData(teacherDatas);
      setCourseData(courseDatas);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const fetchListData = async () => {
    try {
      const courseAllListDatas = await fetchAllCoursesWithIds();
      const teacherAllListDatas = await fetchAllTeachersWithIds();
      setTeacherListData(teacherAllListDatas);
      setCourseListData(courseAllListDatas);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const fetchCenterData = async () => {
    try {
      const centerData = await fetchAllCentersWithIds();
      if (centerLocalId !== null && centerLocalId !== "undefined") {
        setFilters((prevFilters) => ({
          ...prevFilters,
          centerId: centerLocalId,
        }));
        fetchListWithCenterIdData(centerLocalId);
      } else if (centerData !== null && centerData.length > 0) {
        setFilters((prevFilters) => ({
          ...prevFilters,
          centerId: centerData[0].id,
        }));
      }
      setCenterData(centerData);
    } catch (error) {
      toast.error(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchCenterData(); // Fetch center data and subjects

      // Check if local storage has center ID
      if (centerLocalId && centerLocalId !== "undefined") {
        setFilters((prevFilters) => ({
          ...prevFilters,
          centerId: centerLocalId,
        }));
      } else if (centerData && centerData.length > 0) {
        // Use the first center's ID as the default if no center is in local storage
        setFilters((prevFilters) => ({
          ...prevFilters,
          centerId: centerData[0].id,
        }));
      }
    };
    fetchData();
    fetchListData();
  }, [selectedCenter]);

  // const handleCenterChange = async (event) => {
  //   const centerId = event.target.value;

  //   // Update the filters state
  //   setFilters((prevFilters) => ({ ...prevFilters, centerId }));

  //   if (centerId) {
  //     try {
  //       // Fetch the associated data
  //       const courseDatas = await fetchAllCoursesWithIdsC(centerId);
  //       const teacherDatas = await fetchAllTeacherListByCenter(centerId);

  //       // Update the respective state variables
  //       setCourseData(courseDatas);
  //       setTeacherData(teacherDatas);
  //     } catch (error) {
  //       toast.error("Error fetching data: " + error.message);
  //     }
  //   } else {
  //     // Clear dependent data if no center is selected
  //     setCourseData([]);
  //     setTeacherData([]);
  //   }
  // };

  const handleCourseChange = async (event) => {
    const courseId = event.target.value;
    setFilters((prevFilters) => ({ ...prevFilters, courseId })); // Update filter state

    // Only trigger API call if courseId is not empty
    if (courseId) {
      try {
        const classes = await fetchAllClassesWithIdsC(courseId); // Fetch class list based on courseId
        setClassData(classes);
      } catch (error) {
        toast.error(error.message);
      }
    }
  };

  /* -------------------- DATA FETCH -------------------- */
  const getDocumentData = async () => {
    try {
      setLoading(true);
      // Dynamically construct query parameters based on filters
      const queryParams = new URLSearchParams();
      if (filters.centerId !== "0") {
        const effectiveCenterId = filters.centerId
          ? filters.centerId
          : centerLocalId &&
              centerLocalId !== "undefined" &&
              centerLocalId !== "0"
            ? centerLocalId
            : centerData.length > 0
              ? centerData[0].id
              : "";
        if (effectiveCenterId) {
          queryParams.append("centerId", selectedCenter);
        }
      }

      // Loop through other filters and add key-value pairs if they have a value
      for (let key in filters) {
        if (filters[key] && key !== "centerId") {
          queryParams.append(key, filters[key]);
        }
      }
      let response;
      if (selectedCenter === 0 || selectedCenter === "0") {
        response = await api.get(`/getDocumentFolderWithCustomInfo`);
      } else {
        response = await api.get(
          `/getDocumentFolderWithCustomInfo?${queryParams.toString()}`,
        );
      }
      // const response = await api.get(
      //   `/getDocumentFolderWithCustomInfo?${queryParams.toString()}`
      // );
      setData(response.data);
    } catch (error) {
      toast.error("Error Fetching Data : ", error);
    } finally {
      setLoading(false);
      setIsClearFilterClicked(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({ ...prevFilters, [name]: value }));
  };

  const clearFilter = () => {
    // Reset filter state to initial empty values
    setFilters({
      centerId: "",
      courseId: "",
      classId: "",
      userId: "",
      date: "",
      day: "",
    });

    // Fetch data without filters
    setIsClearFilterClicked(true); // Set flag to trigger data fetch without filters
  };

  useEffect(() => {
    getDocumentData();
  }, [filters, selectedCenter]);

  const handleMenuClose = () => setMenuAnchor(null);

  /* -------------------- TABLE INSTANCE -------------------- */
  const table = useMaterialReactTable({
    columns,
    data,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    initialState: {
      pagination: { pageSize: 50, pageIndex: 0 },
      columnVisibility: {
        createdBy: false,
        createdAt: false,
        updatedBy: false,
        updatedAt: false,
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <button
        className="btn btn-sm btn-border"
        onClick={() =>
          exportVisibleColumnsToCSV(table, data, "DocumentFolderList.csv")
        }
      >
        Export CSV
      </button>
    ),
    muiTableBodyRowProps: ({ row }) => ({
      onClick: canRead
        ? () => navigate(`/document/view/${row.original.id}`)
        : undefined,
      style: { cursor: canRead ? "pointer" : "default" },
    }),
  });

  return (
    <div className="container-fluid center my-4 px-2">
      <ol
        className="breadcrumb my-3"
        style={{ listStyle: "none", padding: 0, margin: 0 }}
      >
        <li>
          <Link to="/" className="custom-breadcrumb">
            Home
          </Link>
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li>
          &nbsp;Folder Management
          <span className="breadcrumb-separator"> &gt; </span>
        </li>
        <li className="active breadcrumb-item" aria-current="page">
          &nbsp;Folder
        </li>
      </ol>
      <div className="card">
        <div
          className="d-flex align-items-center justify-content-between p-1 mb-3"
          style={{ background: "#f5f7f9" }}
        >
          <div className="d-flex align-items-center">
            <div className="d-flex">
              <div className="active dot"></div>
            </div>
            <span className="text-muted me-2">
              This database shows the list of{" "}
              <span className="bold" style={{ color: "#287f71" }}>
                Folder
              </span>
            </span>
          </div>
        </div>
        <div className="mb-3">
          <div className="d-lg-flex individual_fliters">
            <div className="form-group mb-0 mb-1">
              <input type="hidden" name="centerId" value={filters.centerId} />
              {/* <select
                className="form-select form-select-sm center_list"
                name="centerId"
                style={{ width: "100%" }}
                onChange={handleCenterChange}
                value={filters.centerId}
              >
                <option>Select the Centre</option>
                {centerData?.map((center) => (
                  <option key={center.id} value={center.id} selected>
                    {center.centerNames}
                  </option>
                ))}
              </select> */}
            </div>
            <div className="form-group mb-0 mb-1 ms-2">
              <select
                className="form-select form-select-sm center_list"
                name="courseId"
                style={{ width: "100%" }}
                onChange={handleCourseChange}
                value={filters.courseId}
              >
                <option value="" disabled>
                  Select the Course
                </option>
                {selectedCenter === "0"
                  ? courseListData &&
                    courseListData.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.courseNames}
                      </option>
                    ))
                  : courseData &&
                    courseData.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.courseNames}
                      </option>
                    ))}
              </select>
            </div>
            <div className="form-group mb-0 mb-1 ms-2">
              <select
                className="form-select form-select-sm center_list"
                name="classId"
                style={{ width: "100%" }}
                onChange={handleFilterChange}
                value={filters.classId}
              >
                <option value="" disabled>
                  Select the Class
                </option>
                {classData &&
                  classData.map((classes) => (
                    <option key={classes.id} value={classes.id}>
                      {classes.classNames}
                    </option>
                  ))}
              </select>
            </div>
            <div className="form-group mb-0 mb-1 ms-2">
              <input
                type="date"
                name="date"
                value={filters.date}
                onChange={handleFilterChange}
                className="form-control form-control-sm center_list"
                style={{ width: "160px" }}
              />
            </div>
            <div className="form-group mb-0 mb-1 ms-2">
              <select
                className="form-select form-select-sm center_list"
                name="userId"
                style={{ width: "100%" }}
                onChange={handleFilterChange}
                value={filters.userId}
              >
                <option value="" disabled>
                  Select the Teacher
                </option>
                {selectedCenter === "0"
                  ? teacherListData &&
                    teacherListData.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.teacherNames}
                      </option>
                    ))
                  : teacherData &&
                    teacherData.map((teacher) => (
                      <option key={teacher.id} value={teacher.id}>
                        {teacher.teacherNames}
                      </option>
                    ))}
              </select>
            </div>
          </div>
          <div className="d-flex justify-content-between">
            <div className="d-lg-flex individual_fliters mt-2">
              <div className="form-group mb-0 mb-1 ms-2">
                <select
                  className="form-select form-select-sm center_list"
                  name="day"
                  style={{ width: "100%" }}
                  onChange={handleFilterChange}
                  value={filters.day}
                >
                  <option value="" disabled>
                    Select a Day
                  </option>
                  <option value="SUNDAY">Sunday</option>
                  <option value="MONDAY">Monday</option>
                  <option value="TUESDAY">Tuesday</option>
                  <option value="WEDNESDAY">Wednesday</option>
                  <option value="THURSDAY">Thursday</option>
                  <option value="FRIDAY">Friday</option>
                  <option value="SATURDAY">Saturday</option>
                </select>
              </div>
              <div className="form-group mb-0 mb-1 ms-2">
                <button
                  type="button"
                  className="btn btn-border btn-sm"
                  onClick={clearFilter}
                >
                  Clear
                </button>
              </div>
            </div>
            {canCreate && (
              <Link to="/document/add">
                <button
                  type="button"
                  className="btn btn-button btn-sm me-2"
                  style={{ fontWeight: "600px !important" }}
                >
                  &nbsp; Add &nbsp;&nbsp; <i className="bx bx-plus"></i>
                </button>
              </Link>
            )}
          </div>
        </div>
        {loading ? (
          <div className="loader-container">
            <div className="loading">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        ) : (
          <>
            <ThemeProvider theme={theme}>
              <MaterialReactTable table={table} />
            </ThemeProvider>

            <Menu
              id="action-menu"
              anchorEl={menuAnchor}
              open={Boolean(menuAnchor)}
              onClose={handleMenuClose}
            >
              {canUpdate && (
                <MenuItem>
                  <DocumentEdit
                    onSuccess={getDocumentData}
                    id={selectedId}
                    handleMenuClose={handleMenuClose}
                  />
                </MenuItem>
              )}
              {canDelete && (
                <MenuItem>
                  <GlobalDelete
                    path={`/deleteDocumentFolder/${selectedId}`}
                    onDeleteSuccess={getDocumentData}
                    onOpen={handleMenuClose}
                  />
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </div>
    </div>
  );
};

export default Document;
